import { Avatar, colors } from "@mui/material";
import { GridApiCommon, GridCellModes, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { enqueueSnackbar } from "notistack";
import { createElement, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbBuilding, TbBuildingAirport, TbCar, TbQuestionMark } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import MemoizedEditAutoComplete from "@/components/table/EditAutoCompletete";
import MemoizedEditableColumn from "@/components/table/EditableColumn";
import { stringToColor } from "@/helpers/strings";
import useDriversContext from "@/providers/data/DriversContext";
import { usePlanActions, usePlanRowChanges } from "@/stores/use-plan-table";
import { DriverType } from "@/types/driver.types";
import { IPlanPassenger } from "@/types/tasks.types";

export const PlanAssignationColumn = ({
    t,
    drivers,
}: {
    t: TFunction;
    drivers: DriverType[];
}): GridColDef<IPlanPassenger<"api">> => {
    return {
        field: "driverId",
        headerName: t("driver"),
        minWidth: 220,
        editable: true,
        flex: 0.5,
        pastedValueParser: (value: string, row) => {
            const error = errors.find((item) => item.cond(row));
            if (error) {
                enqueueSnackbar({
                    message: t(error.text),
                    variant: "error",
                });
                return undefined;
            }
            const driver = drivers.find((item) => {
                return item.name === value || item.id === value;
            });

            return driver?.id || undefined;
        },
        renderHeader: (params) => <TableHeader Component={TbCar} title={params.colDef.headerName!} />,
        valueGetter: (_val, row) => row.driverId,
        renderCell: (params: GridRenderCellParams<IPlanPassenger<"api">>) => {
            return <MemoComponent api={params.api} row={params.row} field={params.field} value={params.value} />;
        },
        renderEditCell: (params) => (
            <MemoEditComponent api={params.api} row={params.row} field={params.field} value={params.value} />
        ),
    };
};

const errors = [
    {
        cond: (row: IPlanPassenger<"api">) => !row.location,
        component: TbBuilding,
        text: "errors.AddLocation",
        field: "location",
    },
    {
        cond: (row: IPlanPassenger<"api">) => !row.airport,
        component: TbBuildingAirport,
        text: "errors.AddAirport",
        field: "airport",
    },
];

const useGetAssignationInfo = ({ row }: { row: IPlanPassenger<"api"> }) => {
    const rowChanges = usePlanRowChanges(row.id);

    const newRow = useMemo(() => ({ ...row, ...(rowChanges || {}) }), [row, rowChanges]);

    const error = useMemo(
        () => errors.find((item) => item.cond(newRow as IPlanPassenger<"api">)),
        [newRow, rowChanges]
    );

    return { error, newRow };
};

const MemoComponent = memo(
    ({ value, row, field, api }: { api: GridApiCommon; field: string; row: IPlanPassenger<"api">; value: string }) => {
        const { newRow, error } = useGetAssignationInfo({ row });

        const handleEdit = useCallback(() => {
            const mode = api.getCellMode(row.id, error?.field || field);
            if (mode !== GridCellModes.Edit) api.startCellEditMode({ field: error?.field || field, id: row.id });
        }, [newRow, field, error]);

        return (
            <MemoizedEditableColumn
                renderHistoryComp={(val) => (val ? <PlanAssignationCell value={val as string} /> : <span>-</span>)}
                handleEdit={handleEdit}
                api={api}
                field={field}
                id={row.id}
                editable
            >
                <PlanAssignationCell error={error} value={value} />
            </MemoizedEditableColumn>
        );
    }
);

export const PlanAssignationCell = memo(
    ({
        value,
        error,
        className,
    }: {
        error?: {
            cond: (row: IPlanPassenger<"api">) => boolean;
            component: IconType;
            text: string;
            field: string;
        };
        className?: string;
        value: string | number;
    }) => {
        const { t } = useTranslation();
        const { drivers } = useDriversContext();
        const driver = useMemo(() => drivers.find((item) => item.id == value), [drivers, value]);

        const color = stringToColor(driver?.name || "", -15);
        const info = useMemo<{
            color: string;
            icon: IconType;
            text: string;
            hasDriver?: boolean;
        }>(
            () =>
                error
                    ? {
                          color: colors.red[500],
                          icon: error.component,
                          text: error.text,
                      }
                    : driver
                      ? {
                            color: `#${color}`,
                            icon: driver.icon || TbCar,
                            hasDriver: true,
                            text: driver.name,
                        }
                      : {
                            color: colors.grey[500],
                            icon: TbQuestionMark,
                            text: "buttons.assignDriver",
                        },
            [color, error, driver]
        );

        return (
            <div
                className={`flex items-center h-full gap-1    text-xs font-semibold ${className}`}
                style={{ color: info.color }}
            >
                <Avatar sx={{ backgroundColor: info.color, color: "white", width: 25, height: 25 }} variant="rounded">
                    {createElement(info.icon)}
                </Avatar>
                {info.hasDriver ? info.text : t(info.text)}
            </div>
        );
    }
);

const MemoEditComponent = memo(
    ({ value, row, field, api }: { api: GridApiCommon; field: string; row: IPlanPassenger<"api">; value: string }) => {
        const { error, newRow } = useGetAssignationInfo({ row });
        const { drivers } = useDriversContext();
        const { processRowUpdate } = usePlanActions();

        const val = newRow.driverId || value;

        const memozedDrivers = useMemo(() => drivers.map((item) => ({ label: item.name, value: item.id })), [drivers]);
        const driver = useMemo(() => drivers.find((item) => item.name === val || item.id === val), [drivers, val]);
        return error ? (
            <PlanAssignationCell className="!pl-4" error={error} value={value} />
        ) : (
            <MemoizedEditAutoComplete
                processRowUpdate={processRowUpdate}
                api={api}
                row={row}
                field={field}
                value={driver?.id || ""}
                options={memozedDrivers}
                optionComp={DriverOption}
                icon={TbCar}
                placeholder="driver"
                renderHistoryComp={(val) => (val ? <PlanAssignationCell value={val as string} /> : <span>-</span>)}
            />
        );
    }
);
function DriverOption({ option }: { option: { label: string; value: string | number } }) {
    return <PlanAssignationCell error={undefined} value={option.value} />;
}
